import React, { useState, useEffect } from 'react';
import { X, BookOpen, GitFork, Star, Loader2, ExternalLink, Check } from 'lucide-react';
import { fetchPublicNotebooks, clonePublicNotebook, PublicNotebookMeta, removeStalePublicNotebook, checkNotebookStillPublic } from '../services/storageService';
import { Language } from '../types';

interface MarketplaceModalProps {
    isOpen: boolean;
    onClose: () => void;
    currentUserId: string;
    language: Language;
    onCloneSuccess: (newNotebookId: string) => void;
}

export const MarketplaceModal: React.FC<MarketplaceModalProps> = ({
    isOpen, onClose, currentUserId, language, onCloneSuccess
}) => {
    const [notebooks, setNotebooks] = useState<PublicNotebookMeta[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [cloningId, setCloningId] = useState<string | null>(null);
    const [clonedId, setClonedId] = useState<string | null>(null);
    const [showCloneOptions, setShowCloneOptions] = useState<string | null>(null);

    useEffect(() => {
        if (!isOpen) return;
        setIsLoading(true);
        fetchPublicNotebooks(30).then(async data => {
            const valid: PublicNotebookMeta[] = [];
            await Promise.all(data.map(async nb => {
                const isStillPublic = await checkNotebookStillPublic(nb.ownerId, nb.notebookId);
                if (isStillPublic) {
                    valid.push(nb);
                } else if (nb.ownerId === currentUserId) {
                    await removeStalePublicNotebook(nb.notebookId);
                }
            }));
            setNotebooks(valid);
            setIsLoading(false);
        });
    }, [isOpen, currentUserId]);

    if (!isOpen) return null;

    const handleClone = async (nb: PublicNotebookMeta, clearContent: boolean) => {
        setCloningId(nb.notebookId);
        setShowCloneOptions(null);
        try {
            const newId = await clonePublicNotebook(nb.ownerId, nb.notebookId, currentUserId, clearContent);
            setClonedId(nb.notebookId);
            setTimeout(() => {
                onCloneSuccess(newId);
                onClose();
            }, 1200);
        } catch (err) {
            console.error("Clone failed", err);
        } finally {
            setCloningId(null);
        }
    };

    const vn = language === 'vietnamese';

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm">
            <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b dark:border-slate-700">
                    <div className="flex items-center gap-2">
                        <BookOpen size={20} className="text-indigo-500" />
                        <h2 className="text-lg font-bold dark:text-white">
                            {vn ? 'Thư viện Notebooks' : 'Notebook Marketplace'}
                        </h2>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                        <X size={20} />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-4">
                    {isLoading ? (
                        <div className="flex items-center justify-center h-40">
                            <Loader2 size={28} className="animate-spin text-indigo-400" />
                        </div>
                    ) : notebooks.length === 0 ? (
                        <div className="text-center text-gray-400 mt-16">
                            <BookOpen size={40} className="mx-auto mb-3 opacity-30" />
                            <p>{vn ? 'Chưa có notebook nào được chia sẻ' : 'No notebooks shared yet'}</p>
                        </div>
                    ) : (
                        <div className="flex flex-col gap-3">
                            {notebooks.map(nb => (
                                <div key={nb.notebookId} className="border dark:border-slate-700 rounded-xl p-4 hover:border-indigo-300 dark:hover:border-indigo-700 transition-colors">
                                    <div className="flex items-start justify-between gap-3">
                                        <div className="flex-1 min-w-0">
                                            <h3 className="font-semibold text-gray-900 dark:text-white truncate">{nb.title}</h3>
                                            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                                                by {nb.ownerName} · {nb.nodeCount} {vn ? 'ghi chú' : 'notes'}
                                            </p>
                                            <div className="flex items-center gap-3 mt-2 text-xs text-gray-400">
                                                <span className="flex items-center gap-1"><GitFork size={11} /> {nb.clones}</span>
                                                <span className="flex items-center gap-1"><Star size={11} /> {nb.stars}</span>
                                            </div>
                                        </div>

                                        <div className="flex flex-col gap-2 shrink-0">
                                            {/* View button */}
                                            <a
                                                href={`https://studyboard.app/?mode=view&uid=${nb.ownerId}&id=${nb.notebookId}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="flex items-center gap-1.5 px-3 py-1.5 text-xs border dark:border-slate-600 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors"
                                            >
                                                <ExternalLink size={12} />
                                                {vn ? 'Xem' : 'View'}
                                            </a>

                                            {/* Clone button */}
                                            {clonedId === nb.notebookId ? (
                                                <span className="flex items-center gap-1 px-3 py-1.5 text-xs bg-green-100 text-green-700 rounded-lg">
                                                    <Check size={12} /> {vn ? 'Đã clone!' : 'Cloned!'}
                                                </span>
                                            ) : cloningId === nb.notebookId ? (
                                                <span className="flex items-center gap-1 px-3 py-1.5 text-xs bg-indigo-50 text-indigo-500 rounded-lg">
                                                    <Loader2 size={12} className="animate-spin" /> {vn ? 'Đang clone...' : 'Cloning...'}
                                                </span>
                                            ) : showCloneOptions === nb.notebookId ? (
                                                <div className="flex flex-col gap-1">
                                                    <button
                                                        onClick={() => handleClone(nb, false)}
                                                        className="px-3 py-1.5 text-xs bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 transition-colors text-left"
                                                    >
                                                        {vn ? '📋 Giữ nguyên' : '📋 Full clone'}
                                                    </button>
                                                    <button
                                                        onClick={() => handleClone(nb, true)}
                                                        className="px-3 py-1.5 text-xs bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors text-left"
                                                    >
                                                        {vn ? '🖼️ Chỉ giữ ảnh' : '🖼️ Images only'}
                                                    </button>
                                                    <button
                                                        onClick={() => setShowCloneOptions(null)}
                                                        className="px-3 py-1.5 text-xs text-gray-400 hover:text-gray-600 transition-colors text-left"
                                                    >
                                                        {vn ? 'Huỷ' : 'Cancel'}
                                                    </button>
                                                </div>
                                            ) : (
                                                <button
                                                    onClick={() => setShowCloneOptions(nb.notebookId)}
                                                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 transition-colors"
                                                >
                                                    <GitFork size={12} />
                                                    {vn ? 'Clone' : 'Clone'}
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
